import { type PropsWithChildren, useState } from 'react';

import { useAuthStore, useGetLoggedInUser } from '@revelio/auth';
import { FeatureFlag } from '@revelio/core';
import { useFirstLoadGateBlocker } from '@revelio/core/first-load-gate';
import { useUnleashFlag } from '@revelio/core/use-unleash-flag';

import {
  APP_PAGE_TYPE,
  usePageVisitTracking,
} from '../../hooks/usePageVisitTracking';
import { useUnleashUserFlagsReady } from '../../shared/unleash-flags/unleash-flags';
import DashboardPage from '../DashboardPage';
import { useGlobalAIAssistant } from '../global-ai-assistant/global-ai-assistant-context';
import {
  selectIsTurnRunning,
  useGlobalAiEngineStore,
} from '../global-ai-assistant/global-ai-assistant-engine-store';
import { GlobalAiChatInput } from '../global-ai-assistant/global-ai-chat-input';
import { GlobalAiConversationSidebar } from '../global-ai-assistant/global-ai-conversation-sidebar';
import styles from './Homepage.module.css';
import { getUserCards, HomepageCards } from './HomepageCards';
import { HomepageChat } from './HomepageChat';

export const Homepage = () => {
  const assistantDisabled = useUnleashFlag(FeatureFlag.DisableAIAssistant);
  // Until Unleash answers for the logged-in user, user-targeted flags (this
  // switch and the destination links) still read the anonymous context, so the
  // page would flash the wrong view. Render nothing and hold the first-load
  // overlay for that window.
  const flagsReadyForUser = useUnleashUserFlagsReady();
  useFirstLoadGateBlocker(!flagsReadyForUser);
  if (!flagsReadyForUser) {
    return null;
  }
  return assistantDisabled ? <HomepageWrapper /> : <HomepageWithAi />;
};

const HomepageWithAi = () => {
  const { transcript } = useGlobalAIAssistant();
  const isTurnRunning = useGlobalAiEngineStore(selectIsTurnRunning);
  const hasConversation = transcript.length > 0 || isTurnRunning;
  const [chatHistoryOpen, setChatHistoryOpen] = useState(false);
  const toggleChatHistoryOpen = () => {
    setChatHistoryOpen((prev) => !prev);
  };

  const closeChatHistory = () => {
    setChatHistoryOpen(false);
  };

  return hasConversation ? (
    <HomepageChat />
  ) : (
    <HomepageWrapper>
      <div className={styles.chatHistoryOverlay}>
        <GlobalAiConversationSidebar
          collapsed={!chatHistoryOpen}
          onSelect={closeChatHistory}
          onClose={closeChatHistory}
          hideNewChatButton
        />
      </div>
      <div className={styles.aiInputContainer}>
        <GlobalAiChatInput
          testId="assistant-home-prompt"
          placeholder="Ask AI about the workforce"
          autoFocus
          wrapClassName={styles.aiInput}
          onToggleChatHistory={toggleChatHistoryOpen}
        />
      </div>
    </HomepageWrapper>
  );
};

const HomepageWrapper = ({ children }: PropsWithChildren) => {
  const { ory } = useAuthStore();
  const firstName = ory?.name ? `${ory.name.split(' ')[0]}` : '';
  const { loggedInUser } = useGetLoggedInUser();
  const isEconomicImpactEnabled = useUnleashFlag(
    FeatureFlag.EconomicImpactNewLayoutApp
  );

  const userTabs = loggedInUser?.tabs ?? [];
  const enabledFeatureFlags = [
    ...(isEconomicImpactEnabled
      ? [FeatureFlag.EconomicImpactNewLayoutApp]
      : []),
  ];
  const cards = getUserCards(userTabs, enabledFeatureFlags);

  // Analytics knew this page as the landing page; renaming it would split
  // its visit history.
  usePageVisitTracking({
    page: 'Landing',
    pageId: 'landing',
    pageType: APP_PAGE_TYPE,
  });

  return (
    <DashboardPage title={`Welcome, ${firstName}!`}>
      <div className={styles.container}>
        <div>
          <h1 className={styles.headline} aria-label="Workforce Terminal">
            <span aria-hidden="true">
              Workforce Term
              <span className={styles.i}>
                ı<span className={styles.tittle}>•</span>
              </span>
              nal
            </span>
          </h1>
          <p className={styles.subtitle}>
            Track companies, analyze markets, and discover talent, all in one
            place.
          </p>
        </div>
        <HomepageCards cards={cards} />
        {children}
      </div>
    </DashboardPage>
  );
};
